import { Component, ErrorInfo, ReactNode } from "react";
import { ErrorPage } from "@/pages/ErrorPage";

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Custom fallback UI */
  fallback?: ReactNode;
  /** Callback when error is caught */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  isOffline: boolean;
}

/**
 * Error boundary component that catches JavaScript errors in child components.
 * Also monitors online/offline status for network error handling.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      isOffline: typeof navigator !== "undefined" ? !navigator.onLine : false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log to console in development
    console.error("ErrorBoundary caught an error:", error, errorInfo);

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // In production, you might want to send to error tracking service
    // e.g., Sentry.captureException(error, { extra: errorInfo });
  }

  componentDidMount(): void {
    // Listen for online/offline events
    window.addEventListener("online", this.handleOnline);
    window.addEventListener("offline", this.handleOffline);
  }

  componentWillUnmount(): void {
    window.removeEventListener("online", this.handleOnline);
    window.removeEventListener("offline", this.handleOffline);
  }

  handleOnline = (): void => {
    this.setState({ isOffline: false });
  };

  handleOffline = (): void => {
    this.setState({ isOffline: true });
  };

  handleRetry = (): void => {
    // Reset error state and try to re-render children
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    const { hasError, isOffline } = this.state;
    const { children, fallback } = this.props;

    // Show offline message if network is unavailable
    if (isOffline) {
      return (
        <ErrorPage
          type="network"
          onRetry={() => {
            // Check if back online
            if (navigator.onLine) {
              this.setState({ isOffline: false });
              window.location.reload();
            }
          }}
        />
      );
    }

    // Show error page if an error was caught
    if (hasError) {
      // Use custom fallback if provided
      if (fallback) {
        return fallback;
      }

      return <ErrorPage type="fatal" onRetry={this.handleRetry} />;
    }

    return children;
  }
}

export default ErrorBoundary;
